// Engineer mobile shell (Session AFF overhaul). Mobile-first, full-screen, installable. Wraps the
// engineer routes in the offline data provider, shows a live connectivity / sync badge -- the
// engineer needs to know at a glance whether their work is saved locally (offline) or synced --
// a read-this-screen-aloud control, and a thumb-reachable bottom tab bar.
import { useEffect, useRef, useState } from "react";
import type { ComponentType } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { House, ClipboardList, CalendarDays, Clock, MessageSquare, Volume2 } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { useScreenReader } from "../../lib/useScreenReader";
import { ConfirmButton } from "../../components/ConfirmButton";
import { ContactValue } from "../../components/ContactValue";
import { ThemeToggle } from "../../components/ThemeToggle";
import { PhoneViewButton } from "../../components/phoneView/PhoneViewButton";
import { PhoneViewHost } from "../../components/phoneView/PhoneViewStage";
import { EngineerDataProvider, useEngineerData } from "./EngineerDataContext";
import { IdleSessionWarning } from "../office/IdleSessionWarning";
import { ELE_LOGO_DATA_URI } from "../../lib/eleLogo";
import { registerPushNavigationHandler, setPushOptOut, unsubscribePush } from "../../lib/pushClient";
import type { EscalatedSubmission } from "../../lib/offline/sync";
import { retryEscalated } from "../../lib/offline/jobsStore";

// The one client-facing ELE number. ContactValue turns it into a tel: link.
const OFFICE_PHONE = "0203 576 4760";

export function EngineerLayout() {
  return (
    <EngineerDataProvider>
      <EngineerShell />
    </EngineerDataProvider>
  );
}

// The shell proper -- inside the provider, so it can read the shared unread-message count for the
// Messages tab badge and drive read-aloud off the live screen content.
function EngineerShell() {
  const { unreadMessages } = useEngineerData();
  const { user } = useAuth();
  const pushNavigate = useNavigate();
  // Session ANZ: route a notification tap to the right screen from ANYWHERE in the engineer app.
  // This listener used to live on PushBanner, which mounts only on the home screen (EngineerHome),
  // so an engineer already on a job, the diary or their timesheet had their window FOCUSED by the
  // service worker and then never navigated -- they were left staring at whatever they had been
  // looking at, with no indication of what the alert was. The office tree has had this at layout
  // level since it was written (OfficeLayout.tsx); this is the engineer half, finally matching.
  useEffect(() => registerPushNavigationHandler((path) => pushNavigate(path)), [pushNavigate]);
  // One screen-reader instance for the whole shell, reading whatever screen is mounted in <main>.
  const contentRef = useRef<HTMLElement | null>(null);
  const reader = useScreenReader(contentRef);

  return (
    // .engineer-ui scopes the light theme (Session AFK) to the engineer shell, the way
    // .office-ui scopes the office; the login/install pages outside this layout carry it too.
    <div className="engineer-ui flex min-h-dvh flex-col bg-ele-base text-ele-body">
      {/* The header (iPhone fix, Session AUS). An engineer on an iPhone could not see or press the theme
          toggle or the read-aloud button. Two causes, both fixed here for every engineer screen at once:
          (1) index.html asks for viewport-fit=cover with a black-translucent status bar, so the installed app
          draws UNDER the clock, the battery and the Dynamic Island, and nothing padded the header clear of
          them: the two controls sat in the top right corner, where iOS takes the tap for the status bar
          (and, in landscape, behind the sensor housing). .ele-engineer-header pads by the safe-area insets.
          (2) The status items (the Office link, the sync badge, "Needs the office") shared one unwrapping
          row with the controls, so on a 375px or 390px phone they pushed the read-aloud button off the
          right edge. The status items now wrap among themselves and the two controls never shrink or move:
          they are a fixed group on the right of the first row, always full size (44px). */}
      <header data-app-header="" className="ele-engineer-header flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-ele-card-border bg-[var(--ele-shell)]">
        {/* The brand mark IS the identity now (the "ELE Engineer" title is retired): logo left, a
            link home, a 44px tap target via padding on the 32px mark. */}
        <Link
          to="/engineer"
          aria-label="ELE home"
          className="-my-1.5 flex min-h-11 flex-none items-center rounded-ele outline-none focus-visible:ring-2 focus-visible:ring-ele-purple"
        >
          <img src={ELE_LOGO_DATA_URI} alt="East London Elevators" className="h-8 w-auto" />
        </Link>
        {/* The status items take whatever width is left and wrap onto a second line rather than push
            the controls off screen. SESSION BEA: the header itself wraps, and the status box keeps its content's
            minimum width (min-w-min, not min-w-0). With min-w-0 the box was squeezed to nothing at 150 percent text
            on a 320 to 390px phone and its items spilled left over the logo ("Office" printed on the mark); now,
            when the logo, status and controls cannot share a row, the controls drop to a second row at the right. */}
        <div className="flex min-w-min flex-1 flex-wrap items-center justify-end gap-x-3" data-testid="engineer-header-status">
          {/* Switch back to the office app: only for an oversight login (admin/office walking the
              PWA), never an engineer -- a genuine engineer login has no office access to switch to
              (App.tsx's /office ProtectedRoute admits only admin/office), so this mirrors the
              OfficeLayout switcher rather than inventing a new rule. */}
          {user && user.role !== "engineer" && (
            <Link
              to="/office"
              title="Switch to the office app"
              className="flex min-h-11 items-center rounded-ele px-1 text-xs text-ele-body underline-offset-2 hover:text-ele-green hover:underline"
            >
              Office
            </Link>
          )}
          <SyncBadge />
        </div>
        {/* The two controls: never shrink, never wrap away, always on screen. */}
        <div className="ml-auto flex flex-none items-center gap-1" data-testid="engineer-header-controls">
          {/* Phone view (Session BDZ): an admin on a laptop only; renders nothing on a phone or for anyone else. */}
          <PhoneViewButton />
          <ThemeToggle />
          <ScreenReaderButton reader={reader} />
        </div>
      </header>

      {/* The warning before an idle sign-out (Session ASB, the review of the fixes, finding 4), for any account here
          that HAS an idle limit (D-SEC-4: 60 minutes for admin and office; engineers have none), which in practice is
          the Director's admin login (D90). Filling in a job sheet is work on the phone, not a request to the server,
          and the unread poll no longer counts as activity, so without this an hour on one sheet ended his session
          mid-sheet with no warning. The poll is deliberately NOT made to count again: that would keep the most
          powerful account's session alive for as long as the app is open, on the least protected device. A banner
          under the header, never over the tab bar or a job sheet's submit bar (IdleSessionWarning's placement). */}
      {user && user.role !== "engineer" && <IdleSessionWarning placement="banner" />}

      <OfflineBanner />

      {/* pb-24 keeps the last of the content clear of the fixed bottom tab bar. */}
      <main ref={contentRef} className="ele-fade-in flex-1 p-4 pb-24">
        <Outlet />
      </main>

      <BottomNav unread={unreadMessages} />
      <PhoneViewHost />
    </div>
  );
}

// Read this screen aloud (Session AFF): Web Speech, progressive enhancement. Disabled (never hidden)
// where the browser has no speechSynthesis, so it is always present but plainly inert. Pulses while
// reading.
function ScreenReaderButton({ reader }: { reader: ReturnType<typeof useScreenReader> }) {
  const { isReading, toggle, supported } = reader;
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={!supported}
      aria-label="Read this screen aloud"
      aria-pressed={isReading}
      title={supported ? "Read this screen aloud" : "Read aloud is not available on this device"}
      className="flex min-h-11 min-w-11 flex-none items-center justify-center rounded-ele outline-none focus-visible:ring-2 focus-visible:ring-ele-purple disabled:opacity-40"
    >
      {/* Colour by class, not a color prop: a lucide stroke is currentColor, so the resting tint
          follows the muted theme token while the reading state keeps the locked ELE blue. */}
      <Volume2
        size={20}
        className={isReading ? "text-ele-loler ele-reading-pulse" : "text-ele-muted"}
      />
    </button>
  );
}

// The fixed bottom tab bar: five thumb-reachable destinations. History left the nav (it now lives on
// the home screen) and the labels are the plain-English "Home / My Jobs / My Calendar / Timesheet /
// Messages". Active = blue icon + bold label + a soft blue wash behind the icon, no edge bar (the app-wide
// no-accent-stripe rule).
function BottomNav({ unread }: { unread: number }) {
  return (
    <nav
      aria-label="Engineer sections"
      className="fixed inset-x-0 bottom-0 z-40 flex border-t border-ele-card-border bg-[var(--ele-shell)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <BottomTab to="/engineer" end icon={House} label="Home" />
      <BottomTab to="/engineer/jobs" icon={ClipboardList} label="My Jobs" />
      <BottomTab to="/engineer/diary" icon={CalendarDays} label="My Calendar" />
      <BottomTab to="/engineer/timesheet" icon={Clock} label="Timesheet" />
      <BottomTab to="/engineer/messages" icon={MessageSquare} label="Messages" badge={unread} />
    </nav>
  );
}

function BottomTab({
  to,
  label,
  icon: Icon,
  end,
  badge = 0,
}: {
  to: string;
  label: string;
  // className as well as color: the tab tints are theme tokens now (Session AFK), and a lucide
  // stroke follows currentColor, so the active/inactive colour is set by class.
  icon: ComponentType<{ size?: number; color?: string; className?: string }>;
  end?: boolean;
  badge?: number;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `group flex min-h-[56px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 pb-1 pt-1.5 font-menu text-[11px] leading-tight transition-colors ${
          isActive ? "font-semibold" : ""
        }`
      }
    >
      {({ isActive }) => {
        // Colour by class, not a color prop: the active blue is the locked LOLER token (the same
        // #2CB9F3 as before) and the inactive tint is the muted theme token, so the bar reads on
        // a light ground as well as a dark one.
        const colour = isActive ? "text-ele-loler" : "text-ele-muted";
        return (
          <>
            {/* The active state is a soft wash behind the icon (a pill), never an edge bar. */}
            <span
              data-testid="engineer-nav-indicator"
              className={`relative flex h-7 w-14 max-w-full items-center justify-center rounded-full transition-colors ${
                isActive ? "bg-[var(--ele-pill-tint-loler)]" : "group-hover:bg-ele-row-hover"
              }`}
            >
              {/* The badge hangs off the ICON, never off the indicator. The indicator is rem-sized
                  (w-14, capped at the tab by max-w-full), so with the phone's text size enlarged it
                  grows to fill the whole tab and a badge anchored to its edge ran past the tab, and on
                  the last tab past the screen (found by the ui-audit font-scale pass). The icon is a
                  fixed 22px box centred in the tab, so the badge always has the tab's own margin to
                  sit in, and it grows leftwards (anchored right) as the count gains digits. */}
              <span className="relative flex">
                <Icon size={22} className={colour} />
                {/* text-white, not the strong token: this sits ON the purple accent fill, where
                    white is correct in both themes (rule 1 of the Session AFK sweep). */}
                {badge > 0 && (
                  <span
                    className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#7d21c7] px-1 text-[10px] font-bold text-white"
                    aria-label={`${badge} unread`}
                  >
                    {badge}
                  </span>
                )}
              </span>
            </span>
            <span className={`max-w-full truncate px-0.5 ${colour}`}>{label}</span>
          </>
        );
      }}
    </NavLink>
  );
}

// Sign out (Session AEH, FIX-2 B1; relocated to the Home screen in Session AFF). A two-step confirm
// (a stray tap never signs an engineer out mid-job), and an offline / unsynced warning: signing out
// on a shared device is fine, but if work is still queued the engineer should sync first. Lives
// inside EngineerDataProvider so it can read the live online/pending state. Bounces to the ENGINEER
// login (not the office Google sign-in). Exported so the Home screen can render it.
export function SignOutButton({ className = "text-sm text-ele-green" }: { className?: string }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { online, pending, escalations } = useEngineerData();
  const [busy, setBusy] = useState(false);
  // Session PN1: signing out KEEPS notifications on by default. A phone's registration belongs to the device and the engineer, not to
  // the session, so a new job still reaches an engineer who is signed out (the push opens sign-in). Ticking this is the deliberate
  // way to stop them on this phone, for a shared or handed-on device.
  const [stopNotifications, setStopNotifications] = useState(false);
  // An escalated submission is unsynced work too, so it warns on sign-out exactly as a pending
  // one does. Signing out does not lose it (IndexedDB survives), but the engineer should know.
  const warn = !online || pending > 0 || escalations.length > 0;

  async function signOut() {
    setBusy(true);
    try {
      // logout() clears the offline caches itself (clearOfflineApiCache, which since Session AEK
      // also drops the browsable site and lift history), so a shared device never carries company
      // data into the next sign-in. The engineer's own queued work survives, deliberately.
      // The registration is removed BEFORE the session ends: the removal is a signed-in call.
      if (stopNotifications) {
        setPushOptOut(true); // a deliberate stop must survive the next sign-in on this phone
        await unsubscribePush();
      }
      await logout();
      navigate("/engineer/login", { replace: true });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <ConfirmButton
        onConfirm={signOut}
        label="Sign out"
        confirmLabel={warn ? "Sign out anyway?" : "Confirm sign out?"}
        busy={busy}
        busyLabel="Signing out..."
        title={
          warn
            ? "You have work that has not synced yet. Sign back in on this device when you have signal to finish syncing."
            : "Sign out of the engineer app."
        }
        className={className}
      />
      <label className="flex min-h-11 items-center gap-2 text-xs text-ele-muted">
        <input type="checkbox" checked={stopNotifications} onChange={(e) => setStopNotifications(e.target.checked)} className="h-4 w-4" />
        Also stop notifications on this phone
      </label>
    </div>
  );
}

// Session BAS: the calm offline line under the header. The badge alone said "OFFLINE" in red, which
// reads as something broken; on site, no signal is normal and nothing is lost. This says so in
// plain words, with how many things are waiting to send. Shown only while offline.
export function OfflineBanner() {
  const { online, pending } = useEngineerData();
  if (online) return null;
  return (
    <div role="status" className="border-b border-ele-card-border bg-ele-card px-4 py-2 text-sm text-ele-body" data-testid="offline-banner">
      No signal. Your work is saved on this phone and will send when you have signal.
      {pending > 0 && <span className="font-heading text-ele-strong">{` ${pending} waiting to send.`}</span>}
    </div>
  );
}

function SyncBadge() {
  const { online, pending, syncing, escalations, syncNow } = useEngineerData();
  const [panelOpen, setPanelOpen] = useState(false);
  const stuck = escalations.length;
  // Session BBO: the office has sorted the record, so put the sheet back in the retry loop and send.
  const retryAndSync = async (jobId: string) => {
    await retryEscalated(jobId);
    await syncNow();
  };

  const label = syncing
    ? "Syncing..."
    : pending > 0
      ? `${pending} to sync`
      : online
        ? "Online"
        : "Offline";
  // Offline or work waiting -> attention colour; all clear -> green. Offline is AMBER, not red
  // (Session BAS): no signal is normal on site and nothing is lost, so it must not read as a fault.
  const tone = !online ? "text-[var(--ele-amber-text)]" : pending > 0 ? "text-ele-blue" : "text-ele-green";

  // Session AEK (D32). A submission that has stopped retrying gets its own control ALONGSIDE the
  // ordinary badge, never instead of it: the connectivity dot is the only always-visible offline
  // signal in this shell, and an engineer going underground with one stuck sheet from last week
  // still needs to know they are offline and that today's work is queued.
  //
  // AMBER deliberately, not red: red reads as "you did something wrong and must fix it", and the
  // whole point of this state is that there is nothing for the engineer to fix. Amber reads
  // "hand it over".
  return (
    <div className="flex min-w-0 flex-wrap items-center justify-end gap-x-2">
      <span className="flex items-center gap-1.5 whitespace-nowrap font-menu text-xs uppercase tracking-wider" aria-live="polite">
        <span aria-hidden className={`inline-block h-2 w-2 rounded-full ${online ? "bg-ele-green" : "bg-[#b8860b]"}`} />
        <span className={tone}>{label}</span>
      </span>
      {stuck > 0 && (
        <>
          <button
            type="button"
            onClick={() => setPanelOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={panelOpen}
            className="flex min-h-11 items-center gap-1.5 whitespace-nowrap rounded-ele px-1 font-menu text-xs tracking-wider text-amber-400 outline-none focus-visible:ring-2 focus-visible:ring-ele-purple"
          >
            <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-amber-400" />
            <span>Needs the office{stuck > 1 ? ` (${stuck})` : ""}</span>
          </button>
          {panelOpen && <EscalationPanel items={escalations} onClose={() => setPanelOpen(false)} onRetry={retryAndSync} />}
        </>
      )}
    </div>
  );
}

// What the engineer sees when they tap "Needs the office": which job sheets are stuck, why in
// plain English, and one tap to ring the office. Never a status code, never a server message.
function EscalationPanel({ items, onClose, onRetry }: { items: EscalatedSubmission[]; onClose: () => void; onRetry: (jobId: string) => Promise<void> }) {
  const [retrying, setRetrying] = useState<string | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Job sheets that need the office"
        onClick={(e) => e.stopPropagation()}
        className="mt-16 w-full max-w-md rounded-ele border border-amber-500 bg-[var(--ele-shell)] p-4"
      >
        <h2 className="font-heading text-lg text-ele-strong">Needs the office</h2>
        <p className="mt-1 text-sm text-ele-body">
          {items.length === 1 ? "This job sheet" : "These job sheets"} could not be sent automatically. Your work is
          still saved on this device. Please call the office so they can sort it out.
        </p>
        <ul className="mt-3 space-y-3">
          {items.map((item) => (
            <li key={item.id} className="rounded-ele border border-ele-purple-accent p-3">
              <p className="font-heading text-sm text-ele-strong">
                {item.eleNumber ?? item.jobNumber ?? "Job sheet"}
                {item.siteName ? ` at ${item.siteName}` : ""}
              </p>
              <p className="mt-1 text-sm text-amber-400">{item.reason}</p>
              <button
                type="button"
                disabled={retrying !== null}
                onClick={() => {
                  setRetrying(item.id);
                  void onRetry(item.id).finally(() => setRetrying(null));
                }}
                className="mt-2 inline-flex min-h-11 items-center rounded-ele border border-ele-field-border px-4 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50"
              >
                {retrying === item.id ? "Sending..." : "Office sorted it? Try sending again"}
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex items-center justify-between gap-3">
          <ContactValue
            value={OFFICE_PHONE}
            kind="phone"
            className="flex min-h-11 items-center rounded-ele border border-ele-green px-3 font-menu text-xs tracking-wide text-ele-green"
          />
          <button
            type="button"
            onClick={onClose}
            className="flex min-h-11 items-center px-3 font-menu text-xs tracking-wide text-ele-body"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
